import * as Form from '@radix-ui/react-form';
import { Box, Flex, Text } from '@radix-ui/themes';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import {
  forwardRef,
  useState,
  useEffect,
  ReactNode,
  InputHTMLAttributes,
} from 'react';
import DatePickerImport from 'react-datepicker';
import '@trashlab/sitesapp/src/styles/vendor/react-datepicker.css';
import { CalendarIcon } from '@radix-ui/react-icons';
import { DateTime } from 'luxon';

const DatePicker =
  (
    DatePickerImport as unknown as {
      default?: typeof DatePickerImport;
    }
  ).default ?? DatePickerImport;

type FormDatePickerProps = {
  name: string;
  label?: string;
  error?: string;
  containerClassName?: string;
  className?: string;
  placeholder?: string;
  required?: boolean;
  compact?: boolean;
  optional?: boolean;
  rightElement?: ReactNode;
  value?: string | undefined;
  onChange?: (date: string | undefined) => void;
  minDate?: DateTime;
  maxDate?: DateTime;
  dateFormat?: string;
  disabled?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>;

export const FormDatePicker = forwardRef<HTMLInputElement, FormDatePickerProps>(
  (
    {
      name,
      label,
      error,
      containerClassName,
      className,
      placeholder = 'Select date',
      required,
      compact,
      optional,
      rightElement,
      value,
      onChange,
      minDate,
      maxDate,
      dateFormat = 'MM/dd/yyyy',
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      value ? DateTime.fromISO(value).toJSDate() : null,
    );

    useEffect(() => {
      setSelectedDate(value ? DateTime.fromISO(value).toJSDate() : null);
    }, [value]);

    const handleChange = (date: Date | null) => {
      setSelectedDate(date);
      if (onChange) {
        onChange(date ? DateTime.fromJSDate(date).toISO() : undefined);
      }
    };

    const size = compact ? 'h-[24px]' : 'h-[46px]';

    return (
      <Form.Field name={name} className={cn('relative', containerClassName)}>
        <Flex direction="column">
          {label && (
            <Form.Label>
              {label}
              {optional && (
                <Text size="2" color="gray">
                  {' '}
                  (Optional)
                </Text>
              )}
            </Form.Label>
          )}
          <Box className="relative">
            <Form.Control asChild>
              <div className="relative">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleChange}
                  className={cn(
                    size,
                    'w-full bg-white rounded-md pointer-events-auto relative',
                    'border border-solid border-[#ced4da]',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500',
                    'no-animate',
                    error && 'border-red-500',
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    className,
                  )}
                  wrapperClassName="w-full"
                  placeholderText={placeholder}
                  minDate={minDate ? minDate.toJSDate() : undefined}
                  maxDate={maxDate ? maxDate.toJSDate() : undefined}
                  dateFormat={dateFormat}
                  required={required}
                  disabled={disabled}
                  customInput={<input ref={ref} {...props} />}
                  showIcon
                  calendarIconClassName="right-2 z-10 top-1/2 -translate-y-1/2"
                  icon={<CalendarIcon />}
                  calendarClassName="no-animate"
                  portalId="root-portal"
                />
              </div>
            </Form.Control>
            {label && (
              <Form.Message
                className="text-red-500 text-xs mt-1"
                match="valueMissing"
              >
                Please select a {label.toLowerCase()}
              </Form.Message>
            )}
            {error && (
              <Text size="1" color="red" className="mt-1">
                {error}
              </Text>
            )}
          </Box>
        </Flex>
      </Form.Field>
    );
  },
);

FormDatePicker.displayName = 'FormDatePicker';

export default FormDatePicker;
